import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUsers } from "../Auth";
import DateInput from "../components/datePicker/DateInput";

const TbTestForm = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserList, setShowUserList] = useState(true);
  const [tbHistory, setTbHistory] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState("");

  const positions = [
    "RN",
    "PT",
    "PTA",
    "OT",
    "OTA",
    "ST",
    "HHA",
    "LVN",
    "MSW",
    "Caregiver",
    "CNA",
    "Office Assistant",
    "ADMIN"
  ];

  const [tbFormData, setTbFormData] = useState({
    employeeId: "",
    employeeName: "",
    socialSecurityNumber: "",
    // TB SCREENING
    hadTuberculosis: "", // "Yes" or "No"
    tuberculosisExplanation: "",
    hadBcgVaccine: "", // "Yes" or "No"
    bcgVaccineYear: "",
    hadPositiveTbTest: "", // "Yes" or "No"
    positiveTbTestDate: "",
    positiveTbTestTreatment: "",
    // Symptoms
    symptomCough: "", // "Yes" or "No"
    symptomNightSweats: "", // "Yes" or "No"
    symptomFever: "", // "Yes" or "No"
    symptomWeightLoss: "", // "Yes" or "No"
    symptomLossOfAppetite: "", // "Yes" or "No"
    screeningSignature: "",
    screeningDate: "",
    // TB SKIN TEST
    healthcareProfessionalName: "",
    datePlaced: "",
    testSite: "", // "Right" or "Left"
    dateRead: "",
    induration: "",
    lotNumber: "",
    expiration: "",
    ppdResult: "", // "(+)" or "(-)"
    administeredByName: "",
    administeredByTitle: "",
    resultsReadByName: "",
    resultsReadByTitle: "",
  });

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      // Exclude file data - we only need user list for selection
      const data = await getUsers(true);
      setUsers(data);
      setLoading(false);
    };
    loadUsers();

    // Load TB history from localStorage
    const savedHistory = localStorage.getItem("tbHistory");
    if (savedHistory) {
      setTbHistory(JSON.parse(savedHistory));
    }
  }, []);

  const handleInputChange = (e) => {
    const { id, name, value, type, checked } = e.target;
    let fieldName = name || id;

    // If position is selected
    if (id === "selectedPosition") {
      setSelectedPosition(value);
      // Reset employee selection when position changes
      setTbFormData(prev => ({
        ...prev,
        employeeId: "",
        employeeName: "",
        socialSecurityNumber: ""
      }));
      return;
    }

    // If employee is selected, auto-populate name and SSN
    if (id === "employeeId") {
      const selectedUser = users.find(u => u._id === value);
      if (selectedUser) {
        const fullName = `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim();
        setTbFormData(prev => ({
          ...prev,
          employeeId: value,
          employeeName: fullName,
          socialSecurityNumber: selectedUser.socialSecurityNumber || ""
        }));
        return;
      }
    }

    setTbFormData(prev => ({
      ...prev,
      [fieldName]: type === "checkbox" ? checked : value
    }));
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "";
    try {
      let date;
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateString);
      }
      if (isNaN(date.getTime())) return "";
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month} / ${day} / ${year}`;
    } catch {
      return "";
    }
  };

  const generateTbPrintFormHTML = (data) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Annual Tuberculosis Form - ${data.employeeName || "Employee"}</title>
        <style>
          @media print {
            @page { 
              margin: 0.4in;
              size: letter;
            }
            body { 
              margin: 0; 
              padding: 0;
            }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 9.5pt;
            line-height: 1.3;
            padding: 15px;
            color: #333;
          }
          .header {
            position: relative;
            margin-bottom: 12px;
            padding-bottom: 10px;
            border-bottom: 3px solid #2c3e50;
            min-height: 100px;
          }
          .logo-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          .header-content {
            text-align: center;
            padding-top: 20px;
          }
          .header h1 {
            font-size: 16pt;
            font-weight: bold;
            margin: 0;
            color: #2c3e50;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .contact-info {
            position: absolute;
            top: 0;
            right: 0;
            font-size: 8pt;
            text-align: right;
            line-height: 1.4;
          }
          .info-section {
            background-color: transparent;
            border: none;
            border-radius: 0;
            padding: 0;
            margin-bottom: 10px;
          }
          .info-row {
            display: flex;
            margin-bottom: 6px;
          }
          .info-row:last-child {
            margin-bottom: 0;
          }
          .info-label {
            font-weight: 600;
            color: #495057;
            min-width: 200px;
            font-size: 10pt;
          }
          .info-value {
            color: #212529;
            font-size: 10pt;
            border-bottom: 1px solid #495057;
            flex: 1;
            padding-bottom: 0;
            min-height: 20px;
          }
          .form-section {
            margin-bottom: 10px;
            border: none;
            border-radius: 0;
            padding: 8px 0;
            background-color: #ffffff;
          }
          .section-title {
            font-size: 11pt;
            font-weight: bold;
            margin-bottom: 8px;
            color: #2c3e50;
            padding-bottom: 0;
            border-bottom: none;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .question-item {
            margin-bottom: 8px;
            padding-bottom: 0;
            border-bottom: none;
          }
          .question-item:last-child {
            margin-bottom: 0;
            padding-bottom: 0;
          }
          .question-text {
            font-weight: 600;
            color: #495057;
            margin-bottom: 4px;
            font-size: 9.5pt;
          }
          .checkbox-group {
            display: inline-block;
            margin-right: 15px;
            font-size: 9.5pt;
          }
          .checkbox-label {
            margin-left: 4px;
            color: #495057;
          }
          .symptom-list {
            margin-top: 6px;
          }
          .symptom-item {
            margin-bottom: 4px;
            padding: 2px 0;
            font-size: 9.5pt;
            color: #495057;
          }
          .signature-row {
            display: flex;
            gap: 15px;
            margin-top: 8px;
            padding-top: 0;
            border-top: none;
          }
          .signature-field {
            flex: 1;
          }
          .signature-label {
            font-weight: 600;
            color: #495057;
            margin-bottom: 3px;
            font-size: 9.5pt;
          }
          .signature-value {
            border-bottom: 1px solid #495057;
            min-height: 20px;
            padding-bottom: 0;
            color: #212529;
            font-size: 9.5pt;
          }
          .two-column {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 8px;
          }
          .form-field {
            margin-bottom: 8px;
          }
          .form-label {
            font-weight: 600;
            color: #495057;
            display: block;
            margin-bottom: 3px;
            font-size: 9.5pt;
          }
          .form-value {
            border-bottom: 1px solid #495057;
            min-height: 20px;
            padding-bottom: 0;
            color: #212529;
            font-size: 9.5pt;
            display: block;
            width: 100%;
          }
          .inline-field {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          input[type="checkbox"], input[type="radio"] {
            width: 14px;
            height: 14px;
            margin: 0;
            vertical-align: middle;
          }
          .explanation-box {
            margin-top: 6px;
            margin-left: 15px;
            padding: 6px;
            background-color: #f8f9fa;
            border-left: 3px solid #2c3e50;
            font-size: 8.5pt;
            color: #495057;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-container">
            <img src="/logo.png.png" alt="Kornivus" style="height: 90px; width: auto; object-fit: contain; max-width: 150px;" onerror="this.style.display='none';" />
          </div>
          <div class="contact-info">
            <div>Phone: 818-888-7772</div>
            <div>Fax: 818-888-7773</div>
            <div>22151 Ventura Blvd., #201</div>
            <div>Woodland Hills, Ca, 91364</div>
          </div>
          <div class="header-content">
            <h1>ANNUAL TUBERCULOSIS FORM</h1>
          </div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="info-label">Name:</span>
            <span class="info-value">${data.employeeName || "________________"}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Social Security Number:</span>
            <span class="info-value">${data.socialSecurityNumber || "________________"}</span>
          </div>
        </div>

        <div class="form-section">
          <div class="section-title">TB SCREENING</div>
          
          <div class="question-item">
            <div class="question-text">1. Have you ever had tuberculosis?</div>
            <div>
              <div class="checkbox-group">
                <input type="checkbox" ${data.hadTuberculosis === "Yes" ? "checked" : ""} disabled> 
                <span class="checkbox-label">Yes</span>
              </div>
              <div class="checkbox-group">
                <input type="checkbox" ${data.hadTuberculosis === "No" ? "checked" : ""} disabled> 
                <span class="checkbox-label">No</span>
              </div>
            </div>
            ${data.hadTuberculosis === "Yes" && data.tuberculosisExplanation ? `
              <div class="explanation-box">
                <strong>If yes, please explain, including date of positive test, circumstances and treatment involved:</strong><br>
                ${data.tuberculosisExplanation}
              </div>
            ` : ""}
          </div>

          <div class="question-item">
            <div class="question-text">2. Have you ever had the BCG vaccine?</div>
            <div>
              <div class="checkbox-group">
                <input type="checkbox" ${data.hadBcgVaccine === "Yes" ? "checked" : ""} disabled> 
                <span class="checkbox-label">Yes</span>
              </div>
              <div class="checkbox-group">
                <input type="checkbox" ${data.hadBcgVaccine === "No" ? "checked" : ""} disabled> 
                <span class="checkbox-label">No</span>
              </div>
            </div>
            ${data.hadBcgVaccine === "Yes" ? `
              <div style="margin-top: 4px;">
                <span style="font-weight: 600; color: #495057; font-size: 9.5pt;">Year received:</span>
                <span class="form-value" style="display: inline-block; min-width: 80px; margin-left: 6px; min-height: 16px;">${data.bcgVaccineYear || ""}</span>
              </div>
            ` : ""}
          </div>

          <div class="question-item">
            <div class="question-text">3. Have you ever had a positive TB skin test?</div>
            <div>
              <div class="checkbox-group">
                <input type="checkbox" ${data.hadPositiveTbTest === "Yes" ? "checked" : ""} disabled> 
                <span class="checkbox-label">Yes</span>
              </div>
              <div class="checkbox-group">
                <input type="checkbox" ${data.hadPositiveTbTest === "No" ? "checked" : ""} disabled> 
                <span class="checkbox-label">No</span>
              </div>
            </div>
            ${data.hadPositiveTbTest === "Yes" ? `
              <div style="margin-top: 4px;">
                <span style="font-weight: 600; color: #495057; font-size: 9.5pt;">Date of positive test:</span>
                <span class="form-value" style="display: inline-block; min-width: 100px; margin-left: 6px; min-height: 16px;">${formatDateShort(data.positiveTbTestDate) || ""}</span>
              </div>
              <div class="explanation-box" style="margin-top: 4px;">
                <strong>If you were treated please include the dates treated and type of treatment:</strong><br>
                ${data.positiveTbTestTreatment || ""}
              </div>
            ` : ""}
          </div>

          <div class="question-item">
            <div class="question-text">4. Do you currently have any of the following symptoms?</div>
            <div class="symptom-list">
              <div class="symptom-item">
                <span>Productive or persistent cough (over 2 weeks duration)</span>
                <div class="checkbox-group" style="margin-left: 15px;">
                  <input type="checkbox" ${data.symptomCough === "Yes" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Yes</span>
                </div>
                <div class="checkbox-group">
                  <input type="checkbox" ${data.symptomCough === "No" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">No</span>
                </div>
              </div>
              <div class="symptom-item">
                <span>Night sweats</span>
                <div class="checkbox-group" style="margin-left: 15px;">
                  <input type="checkbox" ${data.symptomNightSweats === "Yes" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Yes</span>
                </div>
                <div class="checkbox-group">
                  <input type="checkbox" ${data.symptomNightSweats === "No" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">No</span>
                </div>
              </div>
              <div class="symptom-item">
                <span>Fever</span>
                <div class="checkbox-group" style="margin-left: 15px;">
                  <input type="checkbox" ${data.symptomFever === "Yes" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Yes</span>
                </div>
                <div class="checkbox-group">
                  <input type="checkbox" ${data.symptomFever === "No" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">No</span>
                </div>
              </div>
              <div class="symptom-item">
                <span>Weight loss</span>
                <div class="checkbox-group" style="margin-left: 15px;">
                  <input type="checkbox" ${data.symptomWeightLoss === "Yes" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Yes</span>
                </div>
                <div class="checkbox-group">
                  <input type="checkbox" ${data.symptomWeightLoss === "No" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">No</span>
                </div>
              </div>
              <div class="symptom-item">
                <span>Loss of Appetite</span>
                <div class="checkbox-group" style="margin-left: 15px;">
                  <input type="checkbox" ${data.symptomLossOfAppetite === "Yes" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Yes</span>
                </div>
                <div class="checkbox-group">
                  <input type="checkbox" ${data.symptomLossOfAppetite === "No" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">No</span>
                </div>
              </div>
            </div>
          </div>

          <div class="signature-row">
            <div class="signature-field">
              <div class="signature-label">Signature:</div>
              <div class="signature-value">${data.screeningSignature || "________________"}</div>
            </div>
            <div class="signature-field">
              <div class="signature-label">Date:</div>
              <div class="signature-value">${formatDateShort(data.screeningDate) || "________________"}</div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <div class="section-title">TB SKIN TEST</div>
          
          <div class="two-column">
            <div class="form-field">
              <div class="form-label">Healthcare Professional Name:</div>
              <div class="form-value">${data.healthcareProfessionalName || "________________"}</div>
            </div>
            <div class="form-field">
              <div class="form-label">Date Placed:</div>
              <div class="form-value">${formatDateShort(data.datePlaced) || "____/____/____"}</div>
            </div>
          </div>
          <div class="two-column">
            <div class="form-field">
              <div class="form-label">Site:</div>
              <div>
                <div class="checkbox-group">
                  <input type="radio" ${data.testSite === "Right" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Right</span>
                </div>
                <div class="checkbox-group">
                  <input type="radio" ${data.testSite === "Left" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">Left</span>
                </div>
              </div>
            </div>
            <div class="form-field">
              <div class="form-label">Date Read:</div>
              <div class="form-value">${formatDateShort(data.dateRead) || "____/____/____"}</div>
            </div>
          </div>
          <div class="two-column">
            <div class="form-field">
              <div class="form-label">Induration:</div>
              <div class="inline-field">
                <span class="form-value" style="min-width: 80px;">${data.induration || "____"}</span>
                <span style="color: #495057; font-size: 10pt;">mm</span>
              </div>
            </div>
            <div class="form-field">
              <div class="form-label">Lot #:</div>
              <div class="form-value">${data.lotNumber || "________________"}</div>
            </div>
          </div>
          <div class="two-column">
            <div class="form-field">
              <div class="form-label">Exp (Expiration):</div>
              <div class="form-value">${formatDateShort(data.expiration) || "________________"}</div>
            </div>
            <div class="form-field">
              <div class="form-label">PPD Result:</div>
              <div>
                <div class="checkbox-group">
                  <input type="radio" ${data.ppdResult === "(+)" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">(+)</span>
                </div>
                <div class="checkbox-group">
                  <input type="radio" ${data.ppdResult === "(-)" ? "checked" : ""} disabled> 
                  <span class="checkbox-label">(-)</span>
                </div>
              </div>
            </div>
          </div>
          <div class="two-column">
            <div class="form-field">
              <div class="form-label">Administered by:</div>
              <div class="form-value">${data.administeredByName || "________________"}</div>
              <div class="form-label" style="margin-top: 4px;">Title:</div>
              <div class="form-value">${data.administeredByTitle || "________________"}</div>
            </div>
            <div class="form-field">
              <div class="form-label">Results Read by:</div>
              <div class="form-value">${data.resultsReadByName || "________________"}</div>
              <div class="form-label" style="margin-top: 4px;">Title:</div>
              <div class="form-value">${data.resultsReadByTitle || "________________"}</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const printTbForm = (data) => {
    const printHTML = generateTbPrintFormHTML(data);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printHTML);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...tbFormData };

    // Save to TB history
    const historyEntry = {
      id: Date.now(),
      employeeName: tbFormData.employeeName,
      employeeId: tbFormData.employeeId,
      submittedAt: new Date().toISOString(),
      data: submitData
    };

    const updatedHistory = [historyEntry, ...tbHistory];
    setTbHistory(updatedHistory);
    localStorage.setItem('tbHistory', JSON.stringify(updatedHistory));

    // Automatically trigger print, then reset form
    setTimeout(() => {
      printTbForm(submitData);
      resetForm();
      setSelectedPosition("");
      setShowForm(false);
      setShowUserList(true);
    }, 300);
  };

  const resetForm = () => {
    setTbFormData({
      employeeId: "",
      employeeName: "",
      socialSecurityNumber: "",
      hadTuberculosis: "",
      tuberculosisExplanation: "",
      hadBcgVaccine: "",
      bcgVaccineYear: "",
      hadPositiveTbTest: "",
      positiveTbTestDate: "",
      positiveTbTestTreatment: "",
      symptomCough: "",
      symptomNightSweats: "",
      symptomFever: "",
      symptomWeightLoss: "",
      symptomLossOfAppetite: "",
      screeningSignature: "",
      screeningDate: "",
      healthcareProfessionalName: "",
      datePlaced: "",
      testSite: "",
      dateRead: "",
      induration: "",
      lotNumber: "",
      expiration: "",
      ppdResult: "",
      administeredByName: "",
      administeredByTitle: "",
      resultsReadByName: "",
      resultsReadByTitle: "",
    });
    setSelectedPosition("");
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">TB Test Form</h1>
        <button
          onClick={() => navigate("/forms")}
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
        >
          Back to Forms
        </button>
      </div>

      {showUserList && !showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Select Employee</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Position *
            </label>
            <select
              id="selectedPosition"
              value={selectedPosition}
              onChange={handleInputChange}
              className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="">Select a position...</option>
              {positions.map((position) => (
                <option key={position} value={position}>
                  {position}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee Name *
            </label>
            <input
              type="text"
              name="employeeName"
              value={tbFormData.employeeName || ""}
              onChange={handleInputChange}
              className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              placeholder="Enter employee name"
              required
            />
          </div>
          <button
            onClick={() => {
              if (!selectedPosition) {
                alert("Please select a position");
                return;
              }
              if (!tbFormData.employeeName || tbFormData.employeeName.trim() === "") {
                alert("Please enter employee name");
                return;
              }
              setShowUserList(false);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white py-2 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            disabled={!selectedPosition || !tbFormData.employeeName || tbFormData.employeeName.trim() === ""}
          >
            Continue
          </button>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Employee Information Display */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Name *
                  </label>
                  <input
                    type="text"
                    name="employeeName"
                    value={tbFormData.employeeName || ""}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Enter employee name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Social Security Number:
                  </label>
                  <p className="text-sm text-gray-900 font-semibold">{tbFormData.socialSecurityNumber || "N/A"}</p>
                </div>
              </div>
            </div>

            {/* TB SCREENING Section */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">TB SCREENING</h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <strong>1. Have you ever had tuberculosis?</strong>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hadTuberculosis"
                      value="Yes"
                      checked={tbFormData.hadTuberculosis === "Yes"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hadTuberculosis"
                      value="No"
                      checked={tbFormData.hadTuberculosis === "No"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
                {tbFormData.hadTuberculosis === "Yes" && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      If yes, please explain, including date of positive test, circumstances and treatment involved:
                    </label>
                    <textarea
                      name="tuberculosisExplanation"
                      value={tbFormData.tuberculosisExplanation}
                      onChange={handleInputChange}
                      rows="3"
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <strong>2. Have you ever had the BCG vaccine?</strong>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hadBcgVaccine"
                      value="Yes"
                      checked={tbFormData.hadBcgVaccine === "Yes"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hadBcgVaccine"
                      value="No"
                      checked={tbFormData.hadBcgVaccine === "No"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
                {tbFormData.hadBcgVaccine === "Yes" && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year received:
                    </label>
                    <input
                      type="text"
                      name="bcgVaccineYear"
                      value={tbFormData.bcgVaccineYear}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <strong>3. Have you ever had a positive TB skin test?</strong>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hadPositiveTbTest"
                      value="Yes"
                      checked={tbFormData.hadPositiveTbTest === "Yes"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hadPositiveTbTest"
                      value="No"
                      checked={tbFormData.hadPositiveTbTest === "No"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
                {tbFormData.hadPositiveTbTest === "Yes" && (
                  <div className="mt-2 space-y-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date of positive test:
                      </label>
                      <DateInput
                        name="positiveTbTestDate"
                        value={tbFormData.positiveTbTestDate}
                        onChange={handleInputChange}
                        className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        If you were treated please include the dates treated and type of treatment:
                      </label>
                      <textarea
                        name="positiveTbTestTreatment"
                        value={tbFormData.positiveTbTestTreatment}
                        onChange={handleInputChange}
                        rows="3"
                        className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <strong>4. Do you currently have any of the following symptoms?</strong>
                </label>
                <div className="space-y-2">
                  {[
                    { key: "symptomCough", label: "Productive or persistent cough (over 2 weeks duration)" },
                    { key: "symptomNightSweats", label: "Night sweats" },
                    { key: "symptomFever", label: "Fever" },
                    { key: "symptomWeightLoss", label: "Weight loss" },
                    { key: "symptomLossOfAppetite", label: "Loss of Appetite" }
                  ].map((symptom) => (
                    <div key={symptom.key} className="flex items-center gap-4">
                      <span className="text-sm text-gray-700 min-w-[300px]">{symptom.label}</span>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={symptom.key}
                          value="Yes"
                          checked={tbFormData[symptom.key] === "Yes"}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        Yes
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={symptom.key}
                          value="No"
                          checked={tbFormData[symptom.key] === "No"}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        No
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Signature:
                  </label>
                  <input
                    type="text"
                    name="screeningSignature"
                    value={tbFormData.screeningSignature}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date:
                  </label>
                  <DateInput
                    name="screeningDate"
                    value={tbFormData.screeningDate}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>
            </div>

            {/* TB SKIN TEST Section */}
            <div className="border-t border-b border-gray-300 py-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">TB SKIN TEST</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Healthcare Professional Name:
                  </label>
                  <input
                    type="text"
                    name="healthcareProfessionalName"
                    value={tbFormData.healthcareProfessionalName}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Placed:
                  </label>
                  <DateInput
                    name="datePlaced"
                    value={tbFormData.datePlaced}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Site:
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="testSite"
                        value="Right"
                        checked={tbFormData.testSite === "Right"}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      Right
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="testSite"
                        value="Left"
                        checked={tbFormData.testSite === "Left"}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      Left
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Read:
                  </label>
                  <DateInput
                    name="dateRead"
                    value={tbFormData.dateRead}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Induration (mm):
                  </label>
                  <input
                    type="text"
                    name="induration"
                    value={tbFormData.induration}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lot #:
                  </label>
                  <input
                    type="text"
                    name="lotNumber"
                    value={tbFormData.lotNumber}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exp (Expiration):
                  </label>
                  <DateInput
                    name="expiration"
                    value={tbFormData.expiration}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PPD Result:
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="ppdResult"
                        value="(+)"
                        checked={tbFormData.ppdResult === "(+)"}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      (+)
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="ppdResult"
                        value="(-)"
                        checked={tbFormData.ppdResult === "(-)"}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      (-)
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Administered by:
                  </label>
                  <input
                    type="text"
                    name="administeredByName"
                    value={tbFormData.administeredByName}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none mb-2"
                  />
                  <input
                    type="text"
                    name="administeredByTitle"
                    value={tbFormData.administeredByTitle}
                    onChange={handleInputChange}
                    placeholder="Title"
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Results Read by:
                  </label>
                  <input
                    type="text"
                    name="resultsReadByName"
                    value={tbFormData.resultsReadByName}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none mb-2"
                  />
                  <input
                    type="text"
                    name="resultsReadByTitle"
                    value={tbFormData.resultsReadByTitle}
                    onChange={handleInputChange}
                    placeholder="Title"
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-4 border-t border-gray-300">
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
              >
                Submit/Print
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setShowUserList(true);
                  resetForm();
                  setSelectedPosition("");
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default TbTestForm;

